import { deleteBlockedError } from './deleteBlocked'
import { http } from '@/api/http'
import { i18n } from '@/i18n'
import { toListParams, type ListQuery } from '@/api/query'
import type {
  ContentLocale,
  ListResponse,
  ProcurementTransportAllowedTransition,
  ProcurementTransportJumpPreview,
  ProcurementTransportFilterOptions,
  ProcurementTransportUnit,
  ProcurementTransportUnitDeleteCheck,
  ProcurementTransportUnitHistory,
  ProcurementTransportUnitStatusPlan,
  ProcurementUnitType,
} from '@/types/api'
import type { RequestOptions } from '../crud'
import { reportLanguage } from './reports.service'

/** Exactly one source: a planned Batch line or, as in Ucode, a Proforma line. */
export type ProcurementTransportUnitItemInput = {
  batch_item_id?: string
  proforma_item_id?: string
  weight: string
  package_count?: string
  front_count?: string
  rear_count?: string
  package_id?: string
  note?: string
  /** A product-group line: what it holds in this container, product by product with its kilos; weight is their sum. */
  members?: { product_id: string; weight: string }[]
}

export type ProcurementTransportUnitHeadInput = {
  carrier_id?: string
  vehicle_number: string
  driver_name?: string
  is_active: boolean
  assigned_at: string
  note?: string
}

/** A truck's trailer; exactly one is active and becomes trailer_number. */
export type ProcurementTransportUnitTrailerInput = {
  trailer_number: string
  is_active: boolean
}

/** The unit's own logistics price for one route stage (1–3 container, 4–5 truck). */
export type ProcurementTransportUnitStageChargeInput = {
  stage_number: number
  carrier_id: string
  currency_id: string
  /** «Итого». */
  amount: string
  /** «Наличные», the cash part of amount; the rest is «Перечисление», the only logistics in the customs base. */
  cash_amount?: string
  /** «Сумма логиста»; empty = amount. */
  logist_amount?: string
  /** «Логист»: a partner of the logist type in the carrier's currency; empty = none. */
  logist_id?: string
  is_checked: boolean
}

export type ProcurementTransportUnitMutableInput = {
  land_vehicle_type?: 'truck' | 'wagon' | ''
  container_number?: string
  container_type_id?: string
  /** A container's «Release» switch, only kept for now. */
  release_flag?: boolean
  trailer_number?: string
  location_id?: string
  transport_date: string
  water_way_1_arrival_date?: string
  /** «Дни»: the transshipment date is arrival + days; null derives the days from the two dates. */
  water_way_1_days?: number | null
  water_way_1_transshipment_date?: string
  /** «Факт»: the actual departure, a record only. */
  water_way_1_actual_date?: string
  water_way_2_arrival_date?: string
  water_way_2_days?: number | null
  water_way_2_transshipment_date?: string
  water_way_2_actual_date?: string
  /** Stage 1 «Готов на заводе», dated like a water way. */
  factory_arrival_date?: string
  factory_days?: number | null
  factory_transshipment_date?: string
  factory_actual_date?: string
  note?: string
  items: ProcurementTransportUnitItemInput[]
  heads: ProcurementTransportUnitHeadInput[]
  /** Every trailer of a truck with one active; wins over trailer_number. */
  trailers?: ProcurementTransportUnitTrailerInput[]
  stage_charges?: ProcurementTransportUnitStageChargeInput[]
}

export type CreateProcurementTransportUnitInput = ProcurementTransportUnitMutableInput & {
  batch_id: string
  unit_type: ProcurementUnitType
}

export type ReplaceProcurementTransportUnitInput = ProcurementTransportUnitMutableInput & { version: number }
export type PatchProcurementTransportUnitInput = Partial<ProcurementTransportUnitMutableInput> & { version: number }

export type ProcurementTransportStatusTransitionInput = {
  target_proforma_status_id: string
  target_proforma_substatus_id: string
  reason?: string
  version: number
}

const base = '/procurement/transport-units'

export const procurementTransportUnitsService = {
  path: 'procurement/transport-units',
  async list(query?: ListQuery, options?: RequestOptions) {
    const { data } = await http.get<ListResponse<ProcurementTransportUnit>>(base, {
      params: toListParams(query),
      ...options,
    })
    return { count: data.count ?? 0, items: data.items ?? [] }
  },
  async get(id: string, options?: RequestOptions) {
    const { data } = await http.get<ProcurementTransportUnit>(`${base}/${encodeURIComponent(id)}`, options)
    return data
  },
  async create(body: CreateProcurementTransportUnitInput) {
    const { data } = await http.post<ProcurementTransportUnit>(base, body)
    return data
  },
  async replace(id: string, body: ReplaceProcurementTransportUnitInput) {
    const { data } = await http.put<ProcurementTransportUnit>(`${base}/${encodeURIComponent(id)}`, body)
    return data
  },
  async patch(id: string, body: PatchProcurementTransportUnitInput) {
    const { data } = await http.patch<ProcurementTransportUnit>(`${base}/${encodeURIComponent(id)}`, body)
    return data
  },
  async update(id: string, body: PatchProcurementTransportUnitInput) {
    return this.patch(id, body)
  },
  async deleteCheck(id: string, options?: RequestOptions) {
    const { data } = await http.get<ProcurementTransportUnitDeleteCheck>(
      `${base}/${encodeURIComponent(id)}/delete-check`,
      options,
    )
    return data
  },
  async remove(id: string) {
    const [current, check] = await Promise.all([this.get(id), this.deleteCheck(id)])
    if (!check.can_delete) {
      throw deleteBlockedError(check.reasons)
    }
    await http.delete(`${base}/${encodeURIComponent(id)}`, { params: { version: current.version } })
  },
  async history(id: string, page = 1, limit = 20, options?: RequestOptions) {
    const { data } = await http.get<ListResponse<ProcurementTransportUnitHistory>>(
      `${base}/${encodeURIComponent(id)}/history`,
      { params: { page, limit }, ...options },
    )
    return { count: data.count ?? 0, items: data.items ?? [] }
  },
  async filterOptions(options?: RequestOptions) {
    const { data } = await http.get<ProcurementTransportFilterOptions>(`${base}/filter-options`, options)
    return data
  },
  async statusPlans(id: string, options?: RequestOptions) {
    const { data } = await http.get<ProcurementTransportUnitStatusPlan[]>(`${base}/${encodeURIComponent(id)}/status-plans`, options)
    return data ?? []
  },
  /** Sets (a YYYY-MM-DD date) or clears (empty) the planned completion date of a main status. */
  async setStatusPlan(id: string, statusId: string, plannedDate: string, idempotencyKey: string) {
    const path = `${base}/${encodeURIComponent(id)}/status-plans/${encodeURIComponent(statusId)}`
    const headers = { 'Idempotency-Key': idempotencyKey }
    const { data } = plannedDate
      ? await http.put<ProcurementTransportUnitStatusPlan[]>(path, { planned_date: plannedDate }, { headers })
      : await http.delete<ProcurementTransportUnitStatusPlan[]>(path, { headers })
    return data ?? []
  },
  async allowedTransitions(id: string, options?: RequestOptions) {
    const { data } = await http.get<ProcurementTransportAllowedTransition[]>(
      `${base}/${encodeURIComponent(id)}/allowed-transitions`,
      options,
    )
    return data ?? []
  },
  async advance(id: string, body: ProcurementTransportStatusTransitionInput) {
    const { data } = await http.post<ProcurementTransportUnit>(
      `${base}/${encodeURIComponent(id)}/status-transitions/forward`,
      body,
    )
    return data
  },
  /** The steps of a jump to the target and every debt they create or reverse; nothing is saved. */
  async previewJump(id: string, body: ProcurementTransportStatusTransitionInput, options?: RequestOptions) {
    const { data } = await http.post<ProcurementTransportJumpPreview>(
      `${base}/${encodeURIComponent(id)}/status-transitions/preview`,
      body,
      { params: { lang: reportLanguage(i18n.locale) }, ...options },
    )
    return data
  },
  async reverse(id: string, body: ProcurementTransportStatusTransitionInput) {
    const { data } = await http.post<ProcurementTransportUnit>(
      `${base}/${encodeURIComponent(id)}/status-transitions/reverse`,
      body,
    )
    return data
  },
  /** Names in the file follow the content locale. */
  async export(query: ListQuery, locale: ContentLocale) {
    const { data } = await http.post<Blob>(
      `${base}/export`,
      { search: query.search ?? '', filters: query.filters ?? [], sort: query.sort ?? [], lang: reportLanguage(locale) },
      { responseType: 'blob' },
    )
    return data
  },
}
